import ExpandMore from 'common/components/ExpandMore'
import { FeatureKey } from 'common/constants/permission'
import { formatThaiShortDateTwoDigitYear } from 'common/utils/date'
import { fNumber } from 'common/utils/formatNumber'
import { parseISO } from 'date-fns'
import { useIsFishSpecie, useSpeciesName } from 'modules/pond/api/selector'
import MoreAction from 'modules/pond/components/MoreAction'
import { SpeciesName } from 'modules/pond/constants'
import { useGetShrimpSalesTypes } from 'modules/shrimpSales/api/queries'
import { ShrimpSales } from 'modules/shrimpSales/api/types'
import { formatTotalWeight } from 'modules/shrimpSales/utils/saleUnit'
import { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Avatar,
  Box,
  Button,
  Collapse,
  Dialog,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material'

interface ShrimpSalesDetailDialogProps {
  open: boolean
  onClose: () => void
  data?: ShrimpSales
  onClickEdit: () => void
  onClickDelete: () => void
  onClickPhoto: (src: string) => void
}

interface DetailRowProps {
  label: string
  value: ReactNode
  unit?: string
}

const DetailRow = ({ label, value, unit }: DetailRowProps) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.5 }}>
    <Typography fontWeight={600}>{label}</Typography>
    <Stack direction="row" alignItems="baseline" spacing={0.5}>
      <Typography>{value}</Typography>
      {unit && (
        <Typography color="text.secondary" variant="body2">
          {unit}
        </Typography>
      )}
    </Stack>
  </Stack>
)

const ShrimpSalesDetailDialog = ({
  open,
  onClose,
  data,
  onClickEdit,
  onClickDelete,
  onClickPhoto,
}: ShrimpSalesDetailDialogProps) => {
  const { t } = useTranslation('shrimpSales')
  const [expanded, setExpanded] = useState(false)
  const isFish = useIsFishSpecie()
  const speciesName = useSpeciesName()
  const { data: shrimpSalesTypes } = useGetShrimpSalesTypes()

  if (!data) return null

  const typeName = shrimpSalesTypes?.find((el) => el.id === data.shrimpSalesTypeId)?.type ?? '-'
  const totalWeight = formatTotalWeight(data.totalQuantityInKg)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <Box sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Typography variant="h6" fontWeight={700}>
            {t('sale.history')}
          </Typography>
          <MoreAction
            featureKey={FeatureKey.Sales}
            isHideOnCompleted={false}
            onClickEdit={onClickEdit}
            onClickDelete={onClickDelete}
          />
        </Stack>

        <Typography fontWeight={700} sx={{ mt: 1 }}>
          {formatThaiShortDateTwoDigitYear(parseISO(data.date))}
        </Typography>

        <Divider sx={{ mt: 2 }} />
        <Stack divider={<Divider />}>
          <DetailRow label={t('buyer')} value={data.buyerName} />
          <DetailRow label={t('shrimp.type')} value={typeName} />
          <DetailRow
            label={speciesName === SpeciesName.Fish ? t('fish.size') : t('shrimp.size')}
            value={fNumber(data.shrimpCountPerKg)}
            unit={speciesName === SpeciesName.Fish ? t('gram.per.one') : t('shrimp.per.kg')}
          />
          {isFish && (
            <DetailRow
              label={t('amount')}
              value={
                data.totalQuantityCount != null
                  ? fNumber(data.totalQuantityCount, { disabledDecimal: true })
                  : '-'
              }
              unit={t('unit.piece')}
            />
          )}
          <DetailRow
            label={t('total.weight')}
            value={totalWeight.value}
            unit={t(totalWeight.unit === 'ton' ? 'unit.ton' : 'kg')}
          />
          <DetailRow label={t('price')} value={fNumber(data.totalRevenue)} unit={t('baht')} />
          <DetailRow label={t('note')} value={data.note || '-'} />
          <DetailRow
            label={t('photo')}
            value={
              data.billPhotoURL ? (
                <Box sx={{ cursor: 'pointer' }} onClick={() => onClickPhoto(data.billPhotoURL!)}>
                  <Avatar
                    src={data.billPhotoURL}
                    alt={t('view.photo')}
                    sx={{ width: 40, height: 40 }}
                  />
                </Box>
              ) : (
                '-'
              )
            }
          />
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          sx={{ cursor: 'pointer', py: 2 }}
          onClick={() => setExpanded((prev) => !prev)}
        >
          <Typography fontWeight={600}>{t('more.detail')}</Typography>
          <ExpandMore expand={expanded} />
        </Stack>
        <Collapse in={expanded}>
          <Stack spacing={2} sx={{ mb: 2 }}>
            <Box sx={{ borderRadius: 2, bgcolor: 'grey.50', px: 2, py: 1.5 }}>
              <Grid container>
                <Grid item xs={4}>
                  <Typography variant="body2" fontWeight={600}>
                    {t('type')}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" fontWeight={600} align="right">
                    {isFish ? t('amount.piece') : t('amount.kg')}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" fontWeight={600} align="right">
                    {isFish ? t('price.baht.piece') : t('price.baht.kg')}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {(data.categories ?? []).map((category) => (
              <Box
                key={category.id}
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ bgcolor: 'grey.50', px: 2, py: 1.5 }}>
                  <Typography fontWeight={600}>{category.name}</Typography>
                </Box>
                <Stack divider={<Divider />} sx={{ px: 2 }}>
                  {(category.subcategories ?? []).map((sub) => (
                    <Grid container key={sub.id} alignItems="center" sx={{ py: 1.5 }}>
                      <Grid item xs={4}>
                        <Typography variant="body2">{sub.name}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" align="right">
                          {isFish
                            ? sub.quantityCount != null
                              ? fNumber(sub.quantityCount, { disabledDecimal: true })
                              : '-'
                            : fNumber(sub.quantityInKg)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" align="right">
                          {/* บ่อปลาใช้ pricePerCount จาก backend, บ่อกุ้งใช้ pricePerKg —
                              fallback คำนวณเองเฉพาะ record ที่ไม่มี pricePerCount */}
                          {fNumber(
                            isFish
                              ? sub.pricePerCount ?? (sub.pricePerKg * data.shrimpCountPerKg) / 1000
                              : sub.pricePerKg
                          )}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Collapse>

        <Box display="flex" justifyContent="flex-end" mt={1}>
          <Button variant="outlined" color="primary" onClick={onClose}>
            {t('close')}
          </Button>
        </Box>
      </Box>
    </Dialog>
  )
}

export default ShrimpSalesDetailDialog
